"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { StoreCart, StoreCartShippingOption, StoreCustomer } from "@medusajs/types"
import CartMismatchBanner from "@modules/layout/components/cart-mismatch-banner"
import FreeShippingPriceNudge from "@modules/shipping/components/free-shipping-price-nudge"
import { CART_UPDATED_EVENT } from "@lib/util/cart-events"

type CartSessionPayload = {
  cart: StoreCart | null
  customer: StoreCustomer | null
  shippingOptions: StoreCartShippingOption[]
}

export default function CartSessionWidgets() {
  const [data, setData] = useState<CartSessionPayload | null>(null)
  const isMounted = useRef(true)

  const load = useCallback(async () => {
    try {
      const response = await fetch("/api/cart-session", {
        cache: "no-store",
      })

      if (!response.ok) {
        return
      }

      const payload = (await response.json()) as CartSessionPayload
      if (isMounted.current) {
        setData(payload)
      }
    } catch {
      // Ignore session widget failures to avoid blocking page render.
    }
  }, [])

  useEffect(() => {
    load()

    const handleUpdate = () => {
      load()
    }

    window.addEventListener(CART_UPDATED_EVENT, handleUpdate)

    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, handleUpdate)
    }
  }, [load])

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  if (!data) {
    return null
  }

  const { cart, customer, shippingOptions } = data

  return (
    <>
      {customer && cart && (
        <CartMismatchBanner customer={customer} cart={cart} />
      )}
      {cart && shippingOptions?.length ? (
        <FreeShippingPriceNudge
          variant="popup"
          cart={cart}
          shippingOptions={shippingOptions}
        />
      ) : null}
    </>
  )
}
